import React, { useState, useEffect, useCallback } from 'react';
import {
  fetchNitaSurveys,
  deleteNitaSurvey,
  exportNitaSurveyCsv,
} from '../../api/client';
import formatDate from '../../utils/formatDate';
import './NitaSurveyList.css';

const DAYS_OPTIONS = [
  { value: '', label: 'All' },
  { value: '1', label: '1 day' },
  { value: '2', label: '2 days' },
  { value: '3', label: '3 days' },
  { value: '4', label: '4 days' },
  { value: '5', label: '5 days' },
  { value: '6', label: '6 days' },
  { value: '7', label: '7 days' },
];

function outcomeBadgeClass(outcome) {
  if (outcome === 'completed') return 'badge-positive';
  if (outcome === 'partially_completed') return 'badge-pending';
  return 'badge-negative';
}

export default function NitaSurveyList() {
  const [surveys, setSurveys] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [skip, setSkip] = useState(0);
  const [daysFilter, setDaysFilter] = useState('');
  const limit = 20;

  const loadSurveys = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { limit, skip };
      if (daysFilter) params.days_attended = daysFilter;
      const res = await fetchNitaSurveys(params);
      setSurveys(res.data || []);
      setTotal(res.meta?.total || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [skip, daysFilter]);

  useEffect(() => {
    loadSurveys();
  }, [loadSurveys]);

  const toggleExpand = (id) => setExpandedId((prev) => (prev === id ? null : id));

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (confirmId === id) {
      try {
        await deleteNitaSurvey(id);
        setConfirmId(null);
        loadSurveys();
      } catch (err) {
        setError(err.message);
      }
    } else {
      setConfirmId(id);
    }
  };

  const handleCancelDelete = (e) => {
    e.stopPropagation();
    setConfirmId(null);
  };

  const handleExport = async () => {
    setExporting(true);
    setExportError(null);
    try {
      await exportNitaSurveyCsv({ days_attended: daysFilter });
    } catch (err) {
      setExportError(err.message);
    } finally {
      setExporting(false);
    }
  };

  if (loading && surveys.length === 0) {
    return <div className="loading">Loading NITA surveys...</div>;
  }

  if (error) {
    return <div className="error-message">Failed to load NITA surveys: {error}</div>;
  }

  const hasMore = skip + limit < total;
  const hasPrev = skip > 0;

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
        <h3 style={{ margin: 0 }}>NITA Survey Responses</h3>
        <button className="btn-secondary" onClick={handleExport} disabled={exporting}>
          {exporting ? 'Exporting...' : 'Export CSV'}
        </button>
      </div>
      {exportError && (
        <div className="error-message" style={{ marginBottom: '0.5rem' }}>
          Export failed: {exportError}
        </div>
      )}
      <div className="nita-survey-filter">
        <label htmlFor="nita-days-filter">Days attended:</label>
        <select
          id="nita-days-filter"
          value={daysFilter}
          onChange={(e) => { setDaysFilter(e.target.value); setSkip(0); }}
        >
          {DAYS_OPTIONS.map((o) => (
            <option key={o.value || 'all'} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {surveys.length === 0 ? (
        <p>No NITA survey responses yet.</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Name</th>
              <th>Organization</th>
              <th>Days</th>
              <th>Phone</th>
              <th>Outcome</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {surveys.map((s) => (
              <React.Fragment key={s._id}>
                <tr onClick={() => toggleExpand(s._id)} style={{ cursor: 'pointer' }}>
                  <td>{formatDate(s.created_at)}</td>
                  <td>{s.respondent_name || '—'}</td>
                  <td>{s.organization || '—'}</td>
                  <td>
                    {s.days_attended != null ? (
                      <span className="badge badge-neutral">{s.days_attended}</span>
                    ) : '—'}
                  </td>
                  <td style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                    {s.phone_number || 'N/A'}
                  </td>
                  <td>
                    <span className={`badge ${outcomeBadgeClass(s.call_outcome)}`}>
                      {(s.call_outcome || 'unknown').replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td onClick={(e) => e.stopPropagation()} style={{ whiteSpace: 'nowrap' }}>
                    {confirmId === s._id ? (
                      <>
                        <button className="btn-danger-sm" onClick={(e) => handleDelete(e, s._id)}>Confirm</button>
                        {' '}
                        <button className="btn-secondary-sm" onClick={handleCancelDelete}>Cancel</button>
                      </>
                    ) : (
                      <button className="btn-icon" onClick={(e) => handleDelete(e, s._id)} title="Delete">&#128465;</button>
                    )}
                  </td>
                </tr>
                {expandedId === s._id && (
                  <tr className="nita-survey-detail-row">
                    <td colSpan={7}>
                      <div className="nita-survey-details">
                        <div className="nita-survey-meta">
                          <span className="detail-label">Call ID:</span>{' '}
                          <span className="detail-value">{s.call_id || 'N/A'}</span>
                        </div>

                        {s.liked_best && (
                          <div className="nita-survey-section">
                            <div className="nita-survey-section-label">Liked best</div>
                            <div className="nita-survey-text">{s.liked_best}</div>
                          </div>
                        )}
                        {s.improvement && (
                          <div className="nita-survey-section">
                            <div className="nita-survey-section-label">What to improve</div>
                            <div className="nita-survey-text">{s.improvement}</div>
                          </div>
                        )}
                        {s.favorite_session && (
                          <div className="nita-survey-section">
                            <div className="nita-survey-section-label">Favourite / most valuable session</div>
                            <div className="nita-survey-text">{s.favorite_session}</div>
                          </div>
                        )}
                        {s.additional_comments && (
                          <div className="nita-survey-section">
                            <div className="nita-survey-section-label">Anything else for NITA</div>
                            <div className="nita-survey-text">{s.additional_comments}</div>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      )}

      {(hasPrev || hasMore) && (
        <div className="submission-pagination">
          <button
            className="btn-secondary"
            disabled={!hasPrev}
            onClick={() => setSkip((prev) => Math.max(0, prev - limit))}
          >
            Previous
          </button>
          <span className="pagination-info">
            {total === 0 ? 0 : skip + 1}–{Math.min(skip + limit, total)} of {total}
          </span>
          <button
            className="btn-secondary"
            disabled={!hasMore}
            onClick={() => setSkip((prev) => prev + limit)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
